import { SITE_URL, LANG_META, urlFor, pathFor, type Lang } from "./routes";
import { findRoute } from "./allRoutes";

export const ORG_ID = `${SITE_URL}/#organization`;

const aenorCert = (name: string) => ({
  "@type": "EducationalOccupationalCredential",
  name,
  credentialCategory: "certification",
  recognizedBy: { "@type": "Organization", name: "AENOR", url: "https://www.aenor.com" },
});

/** Full Organization block, emitted on every indexable page. */
export const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Techtail",
  legalName: "TECHTAIL RETAILER S.L.",
  url: `${SITE_URL}/`,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo-techtail.png`,
    width: 512,
    height: 512,
  },
  email: "hola@techtail.es",
  telephone: "+34678311074",
  foundingDate: "2024",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Carrer Escultor Llimona, 9",
    postalCode: "08328",
    addressLocality: "Alella",
    addressRegion: "Barcelona",
    addressCountry: "ES",
  },
  parentOrganization: { "@type": "Organization", name: "Grupo Moure", url: "https://grupomoure.com" },
  founder: { "@type": "Person", name: "Sergi Moure", jobTitle: "CEO" },
  areaServed: ["ES", "IT", "Europe"],
  knowsAbout: [
    "tiendas autónomas",
    "visión artificial",
    "control de stock MRO",
    "trazabilidad de material sanitario",
  ],
  hasCredential: [aenorCert("ISO 9001"), aenorCert("ISO 14001")],
  sameAs: ["https://www.linkedin.com/company/techtail-company/"],
};

const website = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: "Techtail",
  publisher: { "@id": ORG_ID },
  inLanguage: Object.values(LANG_META).map((l) => l.hreflang),
};

export type FaqItem = { q: string; a: string };

const PARENT_NAME: Record<string, Partial<Record<Lang, string>> & { es: string }> = {
  retail: { es: "Retail" },
  industrial: { es: "Industrial" },
  recursos: { es: "Recursos", en: "Resources" },
  casos: { es: "Casos", en: "Case studies" },
};
const HOME_NAME: Partial<Record<Lang, string>> & { es: string } = { es: "Inicio", en: "Home" };

/** Article data for resources/cases (author "Techtail" = the Organization). */
export interface ArticleLd {
  headline: string;
  description: string;
  authorName: string;
  authorJobTitle?: string;
  datePublished: string;
  dateModified: string;
  image?: string;
  mentions?: { name: string; url: string }[];
}

/** Builds the JSON-LD blocks for a page. `name` is the page title without brand. */
export function buildJsonLd(lang: Lang, basePath: string, name: string, description: string, faq?: FaqItem[], article?: ArticleLd) {
  const route = findRoute(basePath);
  const path = route ? pathFor(route, lang) : basePath.replace(/^\/+/, "");
  const url = urlFor(lang, path);
  const inLanguage = LANG_META[lang].hreflang;

  if (!route || route.kind === "home") return [organization, website];
  const isContent = route.kind === "hub" || route.kind === "article" || route.kind === "case";

  const blocks: Record<string, unknown>[] = [
    organization,
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: isContent ? HOME_NAME[lang] ?? HOME_NAME.es : "Techtail", item: urlFor(lang, "") },
        ...(route.parent
          ? [{ "@type": "ListItem", position: 2, name: PARENT_NAME[route.parent]?.[lang] ?? PARENT_NAME[route.parent]?.es ?? route.parent, item: urlFor(lang, route.parent) }]
          : []),
        { "@type": "ListItem", position: route.parent ? 3 : 2, name: article?.headline ?? name, item: url },
      ],
    },
  ];

  if (faq && faq.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage,
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (article && (route.kind === "article" || route.kind === "case")) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${url}#article`,
      headline: article.headline,
      description: article.description,
      author:
        article.authorName === "Techtail"
          ? { "@type": "Organization", "@id": ORG_ID, name: "Techtail" }
          : { "@type": "Person", name: article.authorName, ...(article.authorJobTitle ? { jobTitle: article.authorJobTitle } : {}), worksFor: { "@id": ORG_ID } },
      publisher: { "@type": "Organization", "@id": ORG_ID, name: "Techtail" },
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      inLanguage,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      image: article.image ?? route.ogImage,
      ...(article.mentions?.length
        ? { mentions: article.mentions.map((m) => ({ "@type": "Organization", name: m.name, url: m.url })) }
        : {}),
    });
  } else if (route.kind === "division" || route.kind === "venue" || route.kind === "sector" || route.kind === "service") {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      name,
      description,
      url,
      image: route.ogImage,
      inLanguage,
      serviceType: `Techtail ${(route.parent ?? path).charAt(0).toUpperCase()}${(route.parent ?? path).slice(1)}`,
      provider: { "@id": ORG_ID, "@type": "Organization", name: "Techtail" },
      areaServed: "Europe",
    });
  } else if (route.kind === "about") {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name,
      description,
      url,
      inLanguage,
      about: { "@id": ORG_ID },
    });
  }
  return blocks;
}
